import { makeStyles, tokens, Text, Skeleton, SkeletonItem, Image } from '@coreai/fluentui-react';
import { useMemo } from 'react';
import { useConnectorMetadata } from '@/hooks/useConnectionMutations';
import { deriveConnectorDisplayName } from '@/utils/connections/connector';

export interface ConnectorNameProps {
  apiId: string;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  icon: {
    width: '20px',
    height: '20px',
    flexShrink: 0,
    objectFit: 'contain',
  },
  displayName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  skeleton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
});

export const ConnectorName = ({ apiId }: ConnectorNameProps) => {
  const styles = useStyles();
  const { data: connector, isLoading } = useConnectorMetadata(apiId);

  const displayName = useMemo(() => connector?.displayName ?? connector?.name ?? deriveConnectorDisplayName(apiId), [connector, apiId]);
  const iconUri = useMemo(() => connector?.iconUri, [connector]);

  if (isLoading) {
    return (
      <Skeleton className={styles.skeleton} aria-label="Loading connector info">
        <SkeletonItem shape="circle" size={20} />
        <SkeletonItem shape="rectangle" size={16} style={{ width: '80px' }} />
      </Skeleton>
    );
  }

  // Render the connector's branded icon when available. When the metadata
  // endpoint can't supply one, fall back to text only — a generic plug icon
  // adds no information beyond the column header and reads as visual noise.
  return (
    <span className={styles.container}>
      {iconUri && (
        <Image
          src={iconUri}
          alt=""
          className={styles.icon}
          aria-hidden="true"
        />
      )}
      <Text className={styles.displayName}>{displayName}</Text>
    </span>
  );
};

ConnectorName.displayName = 'ConnectorName';
